<template>
  <div class="global-container">
    <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
      <template v-slot:header-options>
        <div class="filters">
          <button v-for="status in STATUSES" :key="status.value" class="filter-button" :class="{ active: isFilterActive('status', status.value) }"
                  :title="'Afficher uniquement les achats : ' + status.label.toLowerCase()" @click="toggleFilter('status', status.value)">
            {{ status.label }}
          </button>
        </div>
        <button @click="exportToExcel" class="action-button ghost only-desktop-flex" type="button" title="Exporter les achats affichés (Excel)"><span class="fas fa-download"></span>Exporter</button>
      </template>
      <template v-slot:table>
        <thead>
        <tr>
          <custom-header class="col-fit" field="invoiceDate">Date</custom-header>
          <custom-header class="col-fit" field="supplierName" :sortable="false">Fournisseur</custom-header>
          <custom-header class="col-fit" field="reference">Pièce</custom-header>
          <custom-header field="label">Libellé</custom-header>
          <custom-header class="col-fit hide-for-mobile" field="account" :sortable="false">Compte</custom-header>
          <custom-header class="col-fit right" field="htAmount">HT</custom-header>
          <custom-header class="col-fit right hide-for-mobile" field="vatAmount">TVA</custom-header>
          <custom-header class="col-fit right" field="ttcAmount">TTC</custom-header>
          <custom-header class="col-fit right hide-for-mobile" field="remainingAmount" :sortable="false">Reste à payer</custom-header>
          <custom-header class="col-fit" field="status">Statut</custom-header>
        </tr>
        </thead>
        <tbody v-if="data && data.length > 0">
        <tr v-if="resumeData" class="total compact-row">
          <td>Total ({{ resumeData.count }})</td>
          <td colspan="4">{{ resumeData.toReviewCount > 0 ? `+ ${resumeData.toReviewCount} à vérifier, non comptabilisé${resumeData.toReviewCount > 1 ? 's' : ''}` : '' }}</td>
          <td class="right">{{ money(resumeData.htTotal) }}</td>
          <td class="right hide-for-mobile">{{ money(resumeData.vatTotal) }}</td>
          <td class="right">{{ money(resumeData.ttcTotal) }}</td>
          <td class="right hide-for-mobile">{{ money(resumeData.remainingTotal) }}</td>
          <td></td>
        </tr>
        <tr v-for="purchase in $refs.table.$refs.searchArray.simpleFilter(data)" :key="purchase.id" class="compact-row" :class="{editable: !isAccountantModule, 'not-booked': isToReview(purchase)}"
            :title="isToReview(purchase) ? 'À vérifier : pas encore comptabilisé' : (isAccountantModule ? undefined : 'Modifier l\'achat')" @click="openPurchaseModal(purchase)">
          <custom-body class="col-fit" field="invoiceDate">{{ displayDate(dateToFec(purchase.invoiceDate)) }}</custom-body>
          <custom-body class="col-fit col-truncate" field="supplierName" :title="purchase.supplierName || '-'">{{ purchase.supplierName || '-' }}</custom-body>
          <custom-body class="col-fit" field="reference"><span v-if="isCreditNote(purchase)" class="doc-chip" title="Avoir fournisseur">Avoir</span> <span class="doc-chip">{{ purchase.reference || '-' }}</span></custom-body>
          <custom-body field="label">{{ purchase.label }}</custom-body>
          <custom-body class="col-fit hide-for-mobile" field="account" :title="purchase.account ? purchase.account.label : undefined"><span class="doc-chip">{{ purchase.account ? purchase.account.num : '-' }}</span></custom-body>
          <custom-body class="col-fit right" field="htAmount">{{ money(signed(purchase, purchase.htAmount)) }}</custom-body>
          <custom-body class="col-fit right hide-for-mobile" field="vatAmount">{{ money(signed(purchase, purchase.vatAmount)) }}</custom-body>
          <custom-body class="col-fit right" field="ttcAmount">{{ money(signed(purchase, purchase.ttcAmount)) }}</custom-body>
          <custom-body class="col-fit right hide-for-mobile" field="remainingAmount">{{ isToReview(purchase) ? '' : money(signed(purchase, purchase.remainingAmount)) }}</custom-body>
          <custom-body class="col-fit" field="status"><StatusBadge :status="purchase.status" :status-enum="statusEnumOf(purchase)" /></custom-body>
        </tr>
        </tbody>
        <tbody v-else><tr class="empty-row"><td colspan="10">Aucun achat enregistré</td></tr></tbody>
      </template>
    </CustomTable>
    <PurchaseModal ref="purchaseModal" :callback="reload" />
  </div>
</template>

<script>
import CustomTable from "@/components/CustomTable.vue";
import {library} from '@fortawesome/fontawesome-svg-core';
import {faDownload} from "@fortawesome/free-solid-svg-icons";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import PurchaseModal from "@/purchase/components/modals/PurchaseModal.vue";
import {AccountingController} from "@/accounting/controllers/AccountingController";
import {AccountingTableMixin} from "@/accounting/services/accountingTableMixin";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {AccountingService} from "@/accounting/services/AccountingService";
import {dateToFec, displayDate, excelDate, money} from "@/accounting/services/export/AccountingFormat";
import {DATE_FORMAT, MONEY_FORMAT} from "@/accounting/services/export/ExcelTable";
import {PURCHASE_CATEGORY_ENUM, PURCHASE_STATUS_ENUM, documentSign, isCreditNote, labelOf, statusEnumOf} from "@/purchase/utils";
library.add(faDownload);

const STATUSES = Object.values(PURCHASE_STATUS_ENUM);
const DATE_FIELD = "invoiceDate";

/**
 * Achats de la période (factures et avoirs des fournisseurs), avec le compte de charge dans lequel chacun est comptabilisé.
 * Un avoir s'affiche en négatif : il vient en déduction des charges et de la TVA déductible.
 * Un achat à vérifier (facture reçue pas encore contrôlée) est listé mais pas comptabilisé ; il est compté à part dans le total.
 */
export default {
  name: 'PurchaseForAccountingTable',
  mixins: [AccountingTableMixin],
  components: {PurchaseModal, StatusBadge, CustomBody, CustomHeader, CustomTable},
  data: function () {
    return {
      STATUSES,
      dateField: DATE_FIELD,
      PURCHASE_STATUS_ENUM,
      data: [],
      resumeData: undefined,
      elements: [
        {label: "Date", value: "invoiceDate", type: "date"},
        {label: "Pièce", value: "reference", type: "text"},
        {label: "Libellé", value: "label", type: "text"},
        {label: "Catégorie", value: "category", type: "select", values: PURCHASE_CATEGORY_ENUM},
        {label: "Montant HT", value: "htAmount", type: "number"},
        {label: "Montant TTC", value: "ttcAmount", type: "number"},
        {label: "Statut", value: "status", type: "select", values: PURCHASE_STATUS_ENUM},
      ],
      searchOptions: {
        filters: AccountingService.periodFilters(DATE_FIELD),
        sorts: [{field: DATE_FIELD, order: SORT_OPERATOR_ENUM.ASCENDING.value}]
      },
    }
  },
  methods: {
    displayDate,
    dateToFec,
    money,
    isCreditNote,
    statusEnumOf,
    signed(purchase, amount) {
      return documentSign(purchase) * (Number(amount) || 0);
    },
    isToReview(purchase) {
      return purchase.status === PURCHASE_STATUS_ENUM.TO_REVIEW.value;
    },
    // Le module comptable consulte les achats des professionnels : pas de modification
    openPurchaseModal(purchase) {
      if (this.isAccountantModule) return;
      this.$refs.purchaseModal.openModal(purchase);
    },
    requestTable(urlParams, professionalId) {
      return AccountingController.getPurchaseTable(urlParams, professionalId);
    },
    requestResume(urlParams, professionalId) {
      return AccountingController.getPurchaseResume(urlParams, professionalId);
    },
    exportToExcel() {
      return this.downloadExcel('Achats', [
        {header: 'Date', pick: row => excelDate(dateToFec(row.invoiceDate)), format: DATE_FORMAT},
        {header: 'Fournisseur', pick: row => row.supplierName},
        {header: 'Type', pick: row => isCreditNote(row) ? 'Avoir' : 'Facture'},
        {header: 'Pièce', pick: row => row.reference},
        {header: 'Libellé', pick: row => row.label},
        {header: 'Catégorie', pick: row => labelOf(PURCHASE_CATEGORY_ENUM, row.category)},
        {header: 'Compte', pick: row => row.account?.num},
        {header: 'Libellé du compte', pick: row => row.account?.label},
        {header: 'HT', pick: row => this.signed(row, row.htAmount), format: MONEY_FORMAT, total: true},
        {header: 'TVA', pick: row => this.signed(row, row.vatAmount), format: MONEY_FORMAT, total: true},
        {header: 'TTC', pick: row => this.signed(row, row.ttcAmount), format: MONEY_FORMAT, total: true},
        {header: 'Payé', pick: row => this.signed(row, row.paidAmount), format: MONEY_FORMAT, total: true},
        {header: 'Reste à payer', pick: row => this.signed(row, row.remainingAmount), format: MONEY_FORMAT, total: true},
        {header: 'Statut', pick: row => labelOf(statusEnumOf(row), row.status)},
      ]);
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';
@require '~/src/accounting/style/accountingTable.styl';
</style>
